import { useId, type ReactNode, type Ref } from 'react'
import type { FieldErrors } from 'react-hook-form'

import { FieldShell } from '@atoms/form/FieldShell'
import { RadioGroup, RadioGroupItem } from '@components/atoms/RadioGroup'
import cn from '@components/utils/cn'
import { useFormField } from '@hooks/useFormField'

export type RadioGroupFieldOption<T extends string> = {
  value: T
  label: ReactNode
  description?: ReactNode
  whenSelected?: ReactNode
}

export type RadioGroupFieldProps<T extends string> = {
  label: ReactNode
  options: RadioGroupFieldOption<T>[]
  value: T | null | undefined
  onValueChange: (value: T) => void
  appearance?: 'list' | 'cards' | 'tiles'
  name?: string
  description?: string
  errors?: FieldErrors
  required?: boolean
  disabled?: boolean
  className?: string
  ref?: Ref<HTMLButtonElement>
}

export function RadioGroupField<T extends string>({
  label,
  options,
  value,
  onValueChange,
  appearance = 'list',
  name,
  description,
  errors,
  required,
  disabled,
  className,
  ref,
}: RadioGroupFieldProps<T>) {
  const id = useId()
  const labelId = `${id}-label`
  const tiles = appearance === 'tiles'
  const cards = appearance === 'cards' || tiles
  const field = useFormField(name ?? id, errors, required, description)

  return (
    <FieldShell field={field} label={label} labelId={labelId} group description={description} className={className}>
      <RadioGroup
        name={name}
        aria-labelledby={labelId}
        aria-describedby={field.ariaDescribedBy}
        aria-invalid={field.ariaInvalid || undefined}
        aria-required={required || undefined}
        disabled={disabled}
        value={value ?? ''}
        onValueChange={(next) => onValueChange(next as T)}
        className={cn(cards ? 'tw:gap-2' : 'tw:gap-1', tiles && 'tw:grid-cols-[repeat(auto-fit,minmax(6rem,1fr))]')}
      >
        {options.map((option, index) => {
          const optionId = `${id}-${option.value}`
          const descriptionId = option.description ? `${optionId}-description` : undefined
          const selected = option.value === value
          return (
            <div key={option.value}>
              <div
                className={cn(
                  'tw:flex tw:gap-3',
                  cards
                    ? 'tw:relative tw:items-center tw:rounded-lg tw:border tw:px-4 tw:py-3 tw:transition-colors'
                    : 'tw:items-start',
                  tiles && 'tw:px-3 tw:py-2',
                  cards &&
                    (selected
                      ? 'tw:border-primary-500 tw:bg-primary-50'
                      : 'tw:border-gray-200 tw:hover:border-primary-300 tw:hover:bg-primary-50')
                )}
              >
                <RadioGroupItem
                  ref={index === 0 ? ref : undefined}
                  value={option.value}
                  id={optionId}
                  aria-describedby={descriptionId}
                  className={cn('tw:shrink-0', !cards && 'tw:mt-0.5')}
                />
                <div className={cn('tw:min-w-0', cards && 'tw:flex tw:flex-1 tw:items-center tw:gap-3')}>
                  <label
                    htmlFor={optionId}
                    className={cn(
                      'tw:m-0 tw:block tw:cursor-pointer tw:text-sm tw:font-medium tw:text-gray-900',
                      cards && "tw:after:absolute tw:after:inset-0 tw:after:content-['']"
                    )}
                  >
                    {option.label}
                  </label>
                  {option.description && (
                    <p
                      id={descriptionId}
                      className={cn(
                        'tw:m-0 tw:text-xs',
                        cards ? 'tw:ml-auto tw:text-gray-500' : 'tw:text-gray-true-500'
                      )}
                    >
                      {option.description}
                    </p>
                  )}
                </div>
              </div>
              {selected && !tiles && option.whenSelected}
            </div>
          )
        })}
      </RadioGroup>
      {tiles && options.find((option) => option.value === value)?.whenSelected}
    </FieldShell>
  )
}
